'use client';

import './styles/@theme';
import { type Ref, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { SiteWideJackpotData } from '@/types/aggregation/site-wide-jackpot';

import { mapSWJInfo } from '@/app/components/site-wide-jackpot/hooks/use-prismic-jackpot-info';
import { useSwjRulesNavigation } from '@/app/components/site-wide-jackpot/hooks/use-swj-rules-navigation';
import { classes } from '@/utils/classes/classes';

import { JackpotCard } from '@/app/components/site-wide-jackpot/subcomponents/jackpot-card';
import { ScrollingBanner } from '@/app/components/site-wide-jackpot/subcomponents/scrolling-banner/scrolling-banner';
import {
    ConfirmationZone,
    type ConfirmationZoneProps
} from '@/app/components/site-wide-jackpot/subcomponents/confirmation-zone/confirmation-zone';
import { useStore } from '@/store/store';
import { SwjRulesInfoButton } from '@/app/components/site-wide-jackpot/subcomponents/swj-rules-info-button/swj-rules-info-button';
import { ROUTES } from '@/utils/constants';
import { useGlobalContext } from '@/store/contexts/global-context';

type DoubleYourBetHeroProps = {
    jackpotData: SiteWideJackpotData;
};

export const DoubleYourBetHero = ({ jackpotData }: DoubleYourBetHeroProps) => {
    const swjData = mapSWJInfo(jackpotData);
    const { isAndroidWrapper, isMobile } = useGlobalContext();
    const openInBrowser = !!swjData.openRulesExternally && isAndroidWrapper;
    const multiplier = useStore((s) => s.siteWideJackpot.multiplier);

    const { handleRulesClick, handleInfoButtonClick } = useSwjRulesNavigation({
        openInBrowser,
        rulesPath: swjData.rulesPath,
        navigationSource: 'category'
    });

    const [isCollapsed, setIsCollapsed] = useState(false);
    const [fixedHeight, setFixedHeight] = useState(100);
    const descriptionRef: Ref<HTMLDivElement> = useRef(null);
    const sentinelRef: Ref<HTMLDivElement> = useRef(null);
    const heroRef: Ref<HTMLDivElement> = useRef(null);

    const confirmationZoneProps: ConfirmationZoneProps = {
        currentMultiplier: multiplier,
        multipliers: swjData.multipliers,
        description: swjData.oneLiner!,
        logoUrl: swjData.alternateLogo!,
        handleRulesClick,
        shortRules: swjData.dybShortRules!,
        jackpotId: swjData.providerJackpotId || ''
    };

    useLayoutEffect(() => {
        if (heroRef.current && descriptionRef.current) {
            const descHeight = descriptionRef.current.offsetHeight;
            setFixedHeight(descHeight);
            descriptionRef.current.style.setProperty('--desc-height', `${descHeight}px`);
            heroRef.current.style.setProperty('--hero-height', `${heroRef.current.offsetHeight}px`);
        }
    }, [isCollapsed, multiplier]);

    useEffect(() => {
        const handleScroll = () => {
            if (!sentinelRef.current) {
                return;
            }

            const { top } = sentinelRef.current.getBoundingClientRect();

            const headerHeight = isMobile ? 66 : 56;

            if (top <= headerHeight && !isCollapsed) {
                setIsCollapsed(true);
            } else if (top > headerHeight && isCollapsed) {
                setIsCollapsed(false);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [isCollapsed, isMobile]);

    return (
        <section ref={heroRef} className={classes('double-your-bet-hero', isCollapsed && 'collapsed')}>
            <div className="hero-top">
                <div className="logo-container">
                    <img src={swjData.logo} alt="site wide jackpot logo" className="logo" />
                    <SwjRulesInfoButton
                        extraClassName="info-button"
                        href={openInBrowser ? swjData.rulesPath || '' : ROUTES.SITE_WIDE_JACKPOT_RULES}
                        openInBrowser={openInBrowser}
                        onClickEvent={handleInfoButtonClick}
                    />
                </div>
                <div className="intro-text">
                    <p className="content-title">{swjData.introTitle}</p>
                    <p className="content-subtitle">{swjData.subHeadline}</p>
                </div>
                <JackpotCard jackpotId={swjData.providerJackpotId!} enableWonXTimesAnimation={true} />
            </div>
            {swjData.providerJackpotId && <ScrollingBanner jackpotId={swjData.providerJackpotId} />}
            {/* Sentinel to track original position */}
            <div ref={sentinelRef} style={{ height: 0 }} />
            {isCollapsed && <div style={{ height: fixedHeight }} />}
            <div ref={descriptionRef} className={classes('description-container', isCollapsed && 'collapsed')}>
                <div className="expanded-content">
                    <ConfirmationZone {...confirmationZoneProps} />
                </div>
                <div className="collapsed-content">
                    <ConfirmationZone
                        {...confirmationZoneProps}
                        scrolledView={true}
                        collapsedOptedOutText={swjData.collapsedOptedOutText}
                    />
                </div>
            </div>
        </section>
    );
};
